import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, BookOpen, Download, Heart, ShoppingBag, 
  Play, ArrowRight, Clock, Star, Trash2 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [purchasesRes, ordersRes, wishlistRes] = await Promise.all([
          axios.get(`${API}/purchases`, { headers: getAuthHeaders(), withCredentials: true }),
          axios.get(`${API}/orders`, { headers: getAuthHeaders(), withCredentials: true }),
          axios.get(`${API}/wishlist`, { headers: getAuthHeaders(), withCredentials: true })
        ]);
        setPurchases(purchasesRes.data);
        setOrders(ordersRes.data);
        setWishlist(wishlistRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${productId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setWishlist(wishlist.filter(w => w.product_id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  const courses = purchases.filter(p => p.product_type === 'course');
  const downloads = purchases.filter(p => p.product_type === 'download');

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="user-dashboard">
        {/* Welcome Section */}
        <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm border border-[#E2E8F0] mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-[#1A202C]">
                Welcome back, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-[#64748B] mt-1">Continue your learning journey</p>
            </div>
            <Link to="/courses">
              <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full" data-testid="explore-more-btn">
                Explore More Courses
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-[#F8F9FA] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2D4A5E]/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-[#2D4A5E]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1A202C]">{courses.length}</div>
                  <div className="text-sm text-[#64748B]">Courses</div>
                </div>
              </div>
            </div>
            <div className="bg-[#F8F9FA] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1B93A4]/10 flex items-center justify-center">
                  <Download className="w-5 h-5 text-[#1B93A4]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1A202C]">{downloads.length}</div>
                  <div className="text-sm text-[#64748B]">Downloads</div>
                </div>
              </div>
            </div>
            <div className="bg-[#F8F9FA] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1A202C]">{wishlist.length}</div>
                  <div className="text-sm text-[#64748B]">Wishlist</div>
                </div>
              </div>
            </div>
            <div className="bg-[#F8F9FA] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1A202C]">{orders.length}</div>
                  <div className="text-sm text-[#64748B]">Orders</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="courses" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="courses" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              My Courses
            </TabsTrigger>
            <TabsTrigger value="downloads" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              My Downloads
            </TabsTrigger>
            <TabsTrigger value="wishlist" className="flex items-center gap-2">
              <Heart className="w-4 h-4" />
              Wishlist
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" />
              Orders
            </TabsTrigger>
          </TabsList>

          {/* Courses Tab */}
          <TabsContent value="courses">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white animate-pulse rounded-xl h-64"></div>
                ))}
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <BookOpen className="w-16 h-16 mx-auto text-[#CBD5E1] mb-4" />
                <h3 className="text-xl font-semibold text-[#1A202C] mb-2">No courses yet</h3>
                <p className="text-[#64748B] mb-6">Start learning by purchasing your first course</p>
                <Link to="/courses">
                  <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full">
                    Browse Courses
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map(item => (
                  <div key={item.product_id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-[#E2E8F0]">
                    <img
                      src={item.product?.thumbnail || 'https://images.unsplash.com/photo-1634117622592-114e3024ff27?w=400'}
                      alt={item.product?.title}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-5">
                      <Badge variant="outline" className="mb-2">{item.product?.category}</Badge>
                      <h3 className="font-semibold text-[#1A202C] line-clamp-2 mb-3">{item.product?.title}</h3>
                      
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#64748B]">Progress</span>
                          <span className="text-[#2D4A5E] font-medium">{item.progress || 0}%</span>
                        </div>
                        <Progress value={item.progress || 0} className="h-2" />
                      </div>

                      <Link to={`/course/${item.product_id}/learn`}>
                        <Button className="w-full bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full" data-testid={`continue-course-${item.product_id}`}>
                          <Play className="w-4 h-4 mr-2" />
                          {item.progress > 0 ? 'Continue' : 'Start'} Learning
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Downloads Tab */}
          <TabsContent value="downloads">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white animate-pulse rounded-xl h-48"></div>
                ))}
              </div>
            ) : downloads.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <Download className="w-16 h-16 mx-auto text-[#CBD5E1] mb-4" />
                <h3 className="text-xl font-semibold text-[#1A202C] mb-2">No downloads yet</h3>
                <p className="text-[#64748B] mb-6">Get started with our templates and resources</p>
                <Link to="/downloads">
                  <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full">
                    Browse Downloads
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {downloads.map(item => (
                  <div key={item.product_id} className="bg-white rounded-xl p-5 shadow-sm border border-[#E2E8F0]">
                    <div className="w-12 h-12 rounded-xl bg-[#1B93A4]/10 flex items-center justify-center mb-4">
                      <Download className="w-6 h-6 text-[#1B93A4]" />
                    </div>
                    <h3 className="font-semibold text-[#1A202C] line-clamp-2 mb-2">{item.product?.title}</h3>
                    <Badge variant="secondary" className="mb-4">{item.product?.file_type?.toUpperCase()}</Badge>
                    <Button variant="outline" className="w-full rounded-full" data-testid={`download-${item.product_id}`}>
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Wishlist Tab */}
          <TabsContent value="wishlist">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white animate-pulse rounded-xl h-24"></div>
                ))}
              </div>
            ) : wishlist.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <Heart className="w-16 h-16 mx-auto text-[#CBD5E1] mb-4" />
                <h3 className="text-xl font-semibold text-[#1A202C] mb-2">Wishlist is empty</h3>
                <p className="text-[#64748B] mb-6">Save products you want to buy later</p>
                <Link to="/courses">
                  <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full">
                    Browse Products
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {wishlist.map(item => (
                  <div key={item.product_id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E2E8F0] flex items-center gap-4">
                    <img
                      src={item.product?.thumbnail || 'https://images.unsplash.com/photo-1634117622592-114e3024ff27?w=200'}
                      alt={item.product?.title}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <Link to={`/product/${item.product_id}`} className="font-semibold text-[#1A202C] hover:text-[#2D4A5E]">
                        {item.product?.title}
                      </Link>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline">{item.product?.category}</Badge>
                        <span className="text-lg font-bold text-[#2D4A5E]">${item.product?.price?.toFixed(2)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromWishlist(item.product_id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white animate-pulse rounded-xl h-32"></div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <ShoppingBag className="w-16 h-16 mx-auto text-[#CBD5E1] mb-4" />
                <h3 className="text-xl font-semibold text-[#1A202C] mb-2">No orders yet</h3>
                <p className="text-[#64748B] mb-6">Your order history will appear here</p>
                <Link to="/courses">
                  <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full">
                    Start Shopping
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <div key={order.order_id} className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <span className="text-sm text-[#64748B]">Order ID</span>
                        <div className="font-mono text-[#1A202C]">{order.order_id}</div>
                      </div>
                      <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                        {order.payment_status}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-[#64748B]">
                        {order.items?.length} item(s) • {new Date(order.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xl font-bold text-[#2D4A5E]">${order.total?.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default DashboardPage;
